/*
 * Copyright (c) 2021, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content."
 */

import { PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';
import {
  BIJIRA_ENTERPRISE_TIER,
  BIJIRA_FREE_TRIAL_TIER,
  BIJIRA_FREE_TRIAL_TIER_LABEL,
  BIJIRA_PAYG_TIER,
  BIJIRA_STARTER_TIER,
  BIJIRA_SUPPORT_TIER,
  CHOREO_SUPPORT_TIER,
  DEVANT_ENTERPRISE_TIER,
  DEVANT_PAYG_TIER,
  DEVANT_STARTER_TIER,
  DEVANT_SUPPORT_TIER,
  DEVELOPER_TIER,
  ENTERPRISE_TIER,
  FREE_TRIAL_TIER,
  PRODUCT_NAME,
  TEAM_TIER,
  TEAM_WITH_SUPPORT_TIER,
} from 'pages/constants';

import { PaymentMethod } from 'types/generated/Billing/models/payment-method';
import { Tier as SubscriptionTier } from 'types/generated/Billing/models/tier';
import { Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Joi, { ValidationError } from 'joi';
import {
  BijiraSubscriptionType,
  ChoreoSubscriptionType,
  CloudType,
  DevantSubscriptionType,
  IComplexTierAttribute,
} from 'data/api/interfaces';
import intl from 'utils/global-intl-provider';
import { FormattedMessage } from 'react-intl';
import {
  bijiraEnterpriseTierUuid,
  choreoTierConfig,
  devantEnterpriseTierUuid,
  enterpriseTierUuid,
} from 'config';

import AmexCardLogo from 'assets/images/amex-logo.svg';
import DiscoverCardLogo from 'assets/images/discover-logo.jpg';
import GenericCardLogo from 'assets/images/generic-logo.svg';
import MasterCardLogo from 'assets/images/mastercard-logo.svg';
import UnionPayCardLogo from 'assets/images/union-pay-logo.jpg';
import VisaCardLogo from 'assets/images/visa-logo.svg';
import JoiPhoneNumber from 'joi-phone-number';
import { AuthSessionVariable, getSessionItem } from './auth';

const joiPhoneValidate = Joi.extend(JoiPhoneNumber);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    blackText: {
      color: 'black',
    },
    supportPlanDescription: {
      color: 'black',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    },
    costDifferenceHelperText: {
      color: theme.palette.grey[300],
    },
  })
);

export function StepDescription() {
  const classes = useStyles();
  return (
    <div className={classes.blackText}>
      <Typography style={{ lineHeight: 0 }} paragraph>
        <FormattedMessage
          id="utils.pageUtils.choreoPlan.step.definition.start"
          defaultMessage="A Step is defined as one of,"
        />
      </Typography>
      <ul>
        <li>
          <FormattedMessage
            id="utils.pageUtils.choreoPlan.step.definition.point1"
            defaultMessage="Maximum of 500ms of compute time on a container having 350MB of Memory, 0.5vCPU"
          />
        </li>
        <li>
          <FormattedMessage
            id="utils.pageUtils.choreoPlan.step.definition.point2"
            defaultMessage="An incoming event, message, or outgoing API call"
          />
        </li>
      </ul>
    </div>
  );
}

export function StepInfo() {
  const classes = useStyles();
  return (
    <div className={classes.blackText}>
      <Typography>
        <FormattedMessage
          id="utils.pageUtils.step.into"
          defaultMessage="Step count is not real time"
        />
      </Typography>
    </div>
  );
}

export function SupportPlanDescription() {
  const classes = useStyles();
  return (
    <div className={classes.supportPlanDescription}>
      <Typography>
        <FormattedMessage
          id="utils.pageUtils.choreoPlan.step.definition.intro"
          defaultMessage={
            "If you don't want a support plan for the moment, you can skip this option." +
            '\nYou can buy a support plan later, upgrade it or cancel it anytime.'
          }
        />
      </Typography>
    </div>
  );
}

export function CostDifferenceHelperText() {
  const classes = useStyles();
  return (
    <div className={classes.costDifferenceHelperText}>
      <Typography>
        <FormattedMessage
          id="utils.pageUtils.cost.difference.helper.text"
          defaultMessage="Cost values are not real-time"
        />
      </Typography>
    </div>
  );
}

export function BillingComponentsHelperText() {
  const classes = useStyles();
  return (
    <div className={classes.blackText}>
      <Typography>
        <FormattedMessage
          id="utils.pageUtils.billing.components.helper.text"
          defaultMessage="The number of components to be billed during the current billing cycle is shown here. 
            Please note that even though a component is deleted during the billing cycle it will get charged."
        />
      </Typography>
    </div>
  );
}

export function SubscriptionDisabledHelperText() {
  return (
    <div>
      <Typography>
        <FormattedMessage
          id="utils.pageUtils.subscription.disable.helper.text"
          defaultMessage="Please cancel the current subscription to change the subscription plan."
        />
      </Typography>
    </div>
  );
}

export function EnterpriseContactUsHelperText() {
  return (
    <div>
      <Typography>
        <FormattedMessage
          id="utils.pageUtils.enterprise.contact.us.helper.text"
          defaultMessage="Sign in using your corporate email address to proceed with this plan."
        />
      </Typography>
    </div>
  );
}

export function convertFromStripePaymentMethodToPaymentMethod(
  stripePaymentMethod: StripePaymentMethod
): PaymentMethod {
  // Adding default values to avoid type incompatibilities.
  // UI ensures that the values of attributes not getting undefined or null.
  return {
    billingDetails: {
      name: stripePaymentMethod.billing_details.name || '',
      email: stripePaymentMethod.billing_details.email || '',
      phone: stripePaymentMethod.billing_details.phone || '',
      address: {
        line1: stripePaymentMethod.billing_details.address?.line1 || '',
        city: stripePaymentMethod.billing_details.address?.city || '',
        country: stripePaymentMethod.billing_details.address?.country || '',
        postalCode:
          stripePaymentMethod.billing_details.address?.postal_code || '',
        state: stripePaymentMethod.billing_details.address?.state || '',
      },
    },
    cardDetails: {
      brand: stripePaymentMethod?.card?.brand || '',
      country: stripePaymentMethod?.card?.country || '',
      expMonth: stripePaymentMethod?.card?.exp_month || 1, // default month - January
      expYear: stripePaymentMethod?.card?.exp_year || 20, // default year - 2020
      funding: stripePaymentMethod?.card?.funding || '',
      last4: stripePaymentMethod?.card?.last4 || '',
    },
    paymentMethodId: stripePaymentMethod.id,
  };
}

export const stripeCountries = [
  { country: 'Afghanistan', code: 'AF' },
  { country: 'Aland Islands', code: 'AX' },
  { country: 'Albania', code: 'AL' },
  { country: 'Algeria', code: 'DZ' },
  { country: 'American Samoa', code: 'AS' },
  { country: 'AndorrA', code: 'AD' },
  { country: 'Angola', code: 'AO' },
  { country: 'Anguilla', code: 'AI' },
  { country: 'Antarctica', code: 'AQ' },
  { country: 'Antigua and Barbuda', code: 'AG' },
  { country: 'Argentina', code: 'AR' },
  { country: 'Armenia', code: 'AM' },
  { country: 'Aruba', code: 'AW' },
  { country: 'Australia', code: 'AU' },
  { country: 'Austria', code: 'AT' },
  { country: 'Azerbaijan', code: 'AZ' },
  { country: 'Bahamas', code: 'BS' },
  { country: 'Bahrain', code: 'BH' },
  { country: 'Bangladesh', code: 'BD' },
  { country: 'Barbados', code: 'BB' },
  { country: 'Belarus', code: 'BY' },
  { country: 'Belgium', code: 'BE' },
  { country: 'Belize', code: 'BZ' },
  { country: 'Benin', code: 'BJ' },
  { country: 'Bermuda', code: 'BM' },
  { country: 'Bhutan', code: 'BT' },
  { country: 'Bolivia', code: 'BO' },
  { country: 'Bosnia and Herzegovina', code: 'BA' },
  { country: 'Botswana', code: 'BW' },
  { country: 'Bouvet Island', code: 'BV' },
  { country: 'Brazil', code: 'BR' },
  { country: 'British Indian Ocean Territory', code: 'IO' },
  { country: 'Brunei Darussalam', code: 'BN' },
  { country: 'Bulgaria', code: 'BG' },
  { country: 'Burkina Faso', code: 'BF' },
  { country: 'Burundi', code: 'BI' },
  { country: 'Cambodia', code: 'KH' },
  { country: 'Cameroon', code: 'CM' },
  { country: 'Canada', code: 'CA' },
  { country: 'Cape Verde', code: 'CV' },
  { country: 'Cayman Islands', code: 'KY' },
  { country: 'Central African Republic', code: 'CF' },
  { country: 'Chad', code: 'TD' },
  { country: 'Chile', code: 'CL' },
  { country: 'China', code: 'CN' },
  { country: 'Christmas Island', code: 'CX' },
  { country: 'Cocos (Keeling) Islands', code: 'CC' },
  { country: 'Colombia', code: 'CO' },
  { country: 'Comoros', code: 'KM' },
  { country: 'Congo', code: 'CG' },
  { country: 'Congo, The Democratic Republic of the', code: 'CD' },
  { country: 'Cook Islands', code: 'CK' },
  { country: 'Costa Rica', code: 'CR' },
  {
    country: "Cote D'Ivoire",
    code: 'CI',
  },
  { country: 'Croatia', code: 'HR' },
  { country: 'Cuba', code: 'CU' },
  { country: 'Cyprus', code: 'CY' },
  { country: 'Czech Republic', code: 'CZ' },
  { country: 'Denmark', code: 'DK' },
  { country: 'Djibouti', code: 'DJ' },
  { country: 'Dominica', code: 'DM' },
  { country: 'Dominican Republic', code: 'DO' },
  { country: 'Ecuador', code: 'EC' },
  { country: 'Egypt', code: 'EG' },
  { country: 'El Salvador', code: 'SV' },
  { country: 'Equatorial Guinea', code: 'GQ' },
  { country: 'Eritrea', code: 'ER' },
  { country: 'Estonia', code: 'EE' },
  { country: 'Ethiopia', code: 'ET' },
  { country: 'Falkland Islands (Malvinas)', code: 'FK' },
  { country: 'Faroe Islands', code: 'FO' },
  { country: 'Fiji', code: 'FJ' },
  { country: 'Finland', code: 'FI' },
  { country: 'France', code: 'FR' },
  { country: 'French Guiana', code: 'GF' },
  { country: 'French Polynesia', code: 'PF' },
  { country: 'French Southern Territories', code: 'TF' },
  { country: 'Gabon', code: 'GA' },
  { country: 'Gambia', code: 'GM' },
  { country: 'Georgia', code: 'GE' },
  { country: 'Germany', code: 'DE' },
  { country: 'Ghana', code: 'GH' },
  { country: 'Gibraltar', code: 'GI' },
  { country: 'Greece', code: 'GR' },
  { country: 'Greenland', code: 'GL' },
  { country: 'Grenada', code: 'GD' },
  { country: 'Guadeloupe', code: 'GP' },
  { country: 'Guam', code: 'GU' },
  { country: 'Guatemala', code: 'GT' },
  { country: 'Guernsey', code: 'GG' },
  { country: 'Guinea', code: 'GN' },
  { country: 'Guinea-Bissau', code: 'GW' },
  { country: 'Guyana', code: 'GY' },
  { country: 'Haiti', code: 'HT' },
  { country: 'Heard Island and Mcdonald Islands', code: 'HM' },
  { country: 'Holy See (Vatican City State)', code: 'VA' },
  { country: 'Honduras', code: 'HN' },
  { country: 'Hong Kong', code: 'HK' },
  { country: 'Hungary', code: 'HU' },
  { country: 'Iceland', code: 'IS' },
  { country: 'India', code: 'IN' },
  { country: 'Indonesia', code: 'ID' },
  { country: 'Iran, Islamic Republic Of', code: 'IR' },
  { country: 'Iraq', code: 'IQ' },
  { country: 'Ireland', code: 'IE' },
  { country: 'Isle of Man', code: 'IM' },
  { country: 'Israel', code: 'IL' },
  { country: 'Italy', code: 'IT' },
  { country: 'Jamaica', code: 'JM' },
  { country: 'Japan', code: 'JP' },
  { country: 'Jersey', code: 'JE' },
  { country: 'Jordan', code: 'JO' },
  { country: 'Kazakhstan', code: 'KZ' },
  { country: 'Kenya', code: 'KE' },
  { country: 'Kiribati', code: 'KI' },
  {
    country: "Korea, Democratic People's Republic of",
    code: 'KP',
  },
  { country: 'Korea, Republic of', code: 'KR' },
  { country: 'Kuwait', code: 'KW' },
  { country: 'Kyrgyzstan', code: 'KG' },
  {
    country: "Lao People's Democratic Republic",
    code: 'LA',
  },
  { country: 'Latvia', code: 'LV' },
  { country: 'Lebanon', code: 'LB' },
  { country: 'Lesotho', code: 'LS' },
  { country: 'Liberia', code: 'LR' },
  { country: 'Libyan Arab Jamahiriya', code: 'LY' },
  { country: 'Liechtenstein', code: 'LI' },
  { country: 'Lithuania', code: 'LT' },
  { country: 'Luxembourg', code: 'LU' },
  { country: 'Macao', code: 'MO' },
  { country: 'Macedonia, The Former Yugoslav Republic of', code: 'MK' },
  { country: 'Madagascar', code: 'MG' },
  { country: 'Malawi', code: 'MW' },
  { country: 'Malaysia', code: 'MY' },
  { country: 'Maldives', code: 'MV' },
  { country: 'Mali', code: 'ML' },
  { country: 'Malta', code: 'MT' },
  { country: 'Marshall Islands', code: 'MH' },
  { country: 'Martinique', code: 'MQ' },
  { country: 'Mauritania', code: 'MR' },
  { country: 'Mauritius', code: 'MU' },
  { country: 'Mayotte', code: 'YT' },
  { country: 'Mexico', code: 'MX' },
  { country: 'Micronesia, Federated States of', code: 'FM' },
  { country: 'Moldova, Republic of', code: 'MD' },
  { country: 'Monaco', code: 'MC' },
  { country: 'Mongolia', code: 'MN' },
  { country: 'Montserrat', code: 'MS' },
  { country: 'Morocco', code: 'MA' },
  { country: 'Mozambique', code: 'MZ' },
  { country: 'Myanmar', code: 'MM' },
  { country: 'Namibia', code: 'NA' },
  { country: 'Nauru', code: 'NR' },
  { country: 'Nepal', code: 'NP' },
  { country: 'Netherlands', code: 'NL' },
  { country: 'Netherlands Antilles', code: 'AN' },
  { country: 'New Caledonia', code: 'NC' },
  { country: 'New Zealand', code: 'NZ' },
  { country: 'Nicaragua', code: 'NI' },
  { country: 'Niger', code: 'NE' },
  { country: 'Nigeria', code: 'NG' },
  { country: 'Niue', code: 'NU' },
  { country: 'Norfolk Island', code: 'NF' },
  { country: 'Northern Mariana Islands', code: 'MP' },
  { country: 'Norway', code: 'NO' },
  { country: 'Oman', code: 'OM' },
  { country: 'Pakistan', code: 'PK' },
  { country: 'Palau', code: 'PW' },
  { country: 'Palestinian Territory, Occupied', code: 'PS' },
  { country: 'Panama', code: 'PA' },
  { country: 'Papua New Guinea', code: 'PG' },
  { country: 'Paraguay', code: 'PY' },
  { country: 'Peru', code: 'PE' },
  { country: 'Philippines', code: 'PH' },
  { country: 'Pitcairn', code: 'PN' },
  { country: 'Poland', code: 'PL' },
  { country: 'Portugal', code: 'PT' },
  { country: 'Puerto Rico', code: 'PR' },
  { country: 'Qatar', code: 'QA' },
  { country: 'Reunion', code: 'RE' },
  { country: 'Romania', code: 'RO' },
  { country: 'Russian Federation', code: 'RU' },
  { country: 'RWANDA', code: 'RW' },
  { country: 'Saint Helena', code: 'SH' },
  { country: 'Saint Kitts and Nevis', code: 'KN' },
  { country: 'Saint Lucia', code: 'LC' },
  { country: 'Saint Pierre and Miquelon', code: 'PM' },
  { country: 'Saint Vincent and the Grenadines', code: 'VC' },
  { country: 'Samoa', code: 'WS' },
  { country: 'San Marino', code: 'SM' },
  { country: 'Sao Tome and Principe', code: 'ST' },
  { country: 'Saudi Arabia', code: 'SA' },
  { country: 'Senegal', code: 'SN' },
  { country: 'Serbia and Montenegro', code: 'CS' },
  { country: 'Seychelles', code: 'SC' },
  { country: 'Sierra Leone', code: 'SL' },
  { country: 'Singapore', code: 'SG' },
  { country: 'Slovakia', code: 'SK' },
  { country: 'Slovenia', code: 'SI' },
  { country: 'Solomon Islands', code: 'SB' },
  { country: 'Somalia', code: 'SO' },
  { country: 'South Africa', code: 'ZA' },
  { country: 'South Georgia and the South Sandwich Islands', code: 'GS' },
  { country: 'Spain', code: 'ES' },
  { country: 'Sri Lanka', code: 'LK' },
  { country: 'Sudan', code: 'SD' },
  { country: 'Suriname', code: 'SR' },
  { country: 'Svalbard and Jan Mayen', code: 'SJ' },
  { country: 'Swaziland', code: 'SZ' },
  { country: 'Sweden', code: 'SE' },
  { country: 'Switzerland', code: 'CH' },
  { country: 'Syrian Arab Republic', code: 'SY' },
  { country: 'Taiwan, Province of China', code: 'TW' },
  { country: 'Tajikistan', code: 'TJ' },
  { country: 'Tanzania, United Republic of', code: 'TZ' },
  { country: 'Thailand', code: 'TH' },
  { country: 'Timor-Leste', code: 'TL' },
  { country: 'Togo', code: 'TG' },
  { country: 'Tokelau', code: 'TK' },
  { country: 'Tonga', code: 'TO' },
  { country: 'Trinidad and Tobago', code: 'TT' },
  { country: 'Tunisia', code: 'TN' },
  { country: 'Turkey', code: 'TR' },
  { country: 'Turkmenistan', code: 'TM' },
  { country: 'Turks and Caicos Islands', code: 'TC' },
  { country: 'Tuvalu', code: 'TV' },
  { country: 'Uganda', code: 'UG' },
  { country: 'Ukraine', code: 'UA' },
  { country: 'United Arab Emirates', code: 'AE' },
  { country: 'United Kingdom', code: 'GB' },
  { country: 'United States', code: 'US' },
  { country: 'United States Minor Outlying Islands', code: 'UM' },
  { country: 'Uruguay', code: 'UY' },
  { country: 'Uzbekistan', code: 'UZ' },
  { country: 'Vanuatu', code: 'VU' },
  { country: 'Venezuela', code: 'VE' },
  { country: 'Viet Nam', code: 'VN' },
  { country: 'Virgin Islands, British', code: 'VG' },
  { country: 'Virgin Islands, U.S.', code: 'VI' },
  { country: 'Wallis and Futuna', code: 'WF' },
  { country: 'Western Sahara', code: 'EH' },
  { country: 'Yemen', code: 'YE' },
  { country: 'Zambia', code: 'ZM' },
  { country: 'Zimbabwe', code: 'ZW' },
];

export const USAStateNames = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function getCountryNameFromCountryCode(code?: string): string | null {
  const filteredResult = stripeCountries.filter(
    (country) => country.code === code
  );
  if (filteredResult.length > 0) {
    return filteredResult[0].country;
  }
  return null;
}

export function validateEmail(inputValue: string): ValidationError | undefined {
  const validationSchema = {
    email: Joi.string().email({ tlds: { allow: false } }),
  };
  return validationSchema.email.validate(inputValue).error;
}

export function validatePhoneNumber(
  inputValue: string
): ValidationError | undefined {
  const validationSchema = {
    phoneNumber: joiPhoneValidate
      .string()
      .phoneNumber({ format: 'international', strict: true }),
  };

  return validationSchema.phoneNumber.validate(inputValue).error;
}

export function getCloudType(): CloudType {
  const path = window.location.pathname;
  if (path.includes('choreo')) {
    return CloudType.Choreo;
  }
  if (path.includes('asgardeo')) {
    return CloudType.Asgardeo;
  }
  if (path.includes('bijira')) {
    return CloudType.Bijira;
  }
  if (path.includes('devant')) {
    return CloudType.Devant;
  }

  return getSessionItem(AuthSessionVariable.CLOUD_TYPE) as CloudType;
}

export const choreoEnterpriseTier: SubscriptionTier = {
  tierId: 'choreo_ep_tier',
  name: ENTERPRISE_TIER,
};

export const bijiraEnterpriseTier: SubscriptionTier = {
  tierId: bijiraEnterpriseTierUuid,
  name: BIJIRA_ENTERPRISE_TIER,
};

export const devantEnterpriseTier: SubscriptionTier = {
  tierId: devantEnterpriseTierUuid,
  name: DEVANT_ENTERPRISE_TIER,
};

export function getStepBasedChoreoPaygDescription(): string {
  return intl.formatMessage({
    id: 'utils.pageUtils.choreo.tierDescription.payAsYouGo.stepBased',
    defaultMessage:
      'The best plan to get extra steps without commitments with unlimited active containers and unlimited developers.',
  });
}

export function getChoreoTierDescription(
  tierName: string,
  isStepBased?: boolean
): string {
  switch (tierName) {
    case DEVELOPER_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.choreo.tierDescription.developer',
        defaultMessage: 'Ideal for developers.',
      });
    case FREE_TRIAL_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.choreo.tierDescription.freeTrial',
        defaultMessage: 'Try all features free for 14 days with 5 components.',
      });
    case TEAM_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.choreo.tierDescription.team',
        defaultMessage:
          'Ideal for teams collaboratively building cloud native apps. ',
      });
    case ENTERPRISE_TIER:
      if (isStepBased) {
        return intl.formatMessage(
          {
            id: 'utils.pageUtils.choreo.tierDescription.enterprise.stepBased',
            defaultMessage:
              'The best plan to get the maximum out of the {productName}. If you want more details ' +
              "on the latest component-based enterprise plans, click 'Contact Us' and fill " +
              'in your details. We will contact you soon.',
          },
          { productName: PRODUCT_NAME }
        );
      }
      return intl.formatMessage({
        id: 'utils.pageUtils.choreo.tierDescription.enterprise',
        defaultMessage:
          'Ideal for companies building large-scale distributed apps.',
      });
    case TEAM_WITH_SUPPORT_TIER: {
      return intl.formatMessage({
        id: 'utils.pageUtils.choreo.tierDescription.teamWithSupport',
        defaultMessage:
          'The best plan to get extra components together with a support plan.',
      });
    }
    default:
      return '';
  }
}

export function getChoreoPricingDetails(tierName: string) {
  switch (tierName) {
    case DEVELOPER_TIER:
      return {
        price: '$0',
        description: intl.formatMessage({
          id: 'utils.pageUtils.choreo.tierPricing.developer',
          defaultMessage: 'for first 5 components',
        }),
      };
    case FREE_TRIAL_TIER:
      return {
        price: '$0',
        description: intl.formatMessage({
          id: 'utils.pageUtils.choreo.tierPricing.freeTrial',
          defaultMessage: '14-day free trial',
        }),
      };
    case TEAM_TIER:
      return {
        price: `$${choreoTierConfig.payAsyouGo.pricePerComponent}`,
        description: intl.formatMessage({
          id: 'utils.pageUtils.choreo.tierPricing.team',
          defaultMessage: 'per component per month',
        }),
      };
    case ENTERPRISE_TIER:
      return {
        price: 'Custom',
        description: intl.formatMessage({
          id: 'utils.pageUtils.choreo.tierPricing.enterprise',
          defaultMessage: 'Discounts offered based on annual commitments',
        }),
      };
    case CHOREO_SUPPORT_TIER:
      return {
        price: `$${choreoTierConfig.choreoSupport.basePrice}/month`,
        description: intl.formatMessage(
          {
            id: 'utils.pageUtils.choreo.tierPricing.support',
            defaultMessage: '+ 2% of monthly {productName} charges ',
          },
          { productName: PRODUCT_NAME }
        ),
      };
    default:
      return { price: 'N/A', description: '' };
  }
}

export function getDevantTierDescription(tierName: string): string {
  switch (tierName) {
    case DEVANT_STARTER_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.devant.tierDescription.starter',
        defaultMessage:
          'Ideal for students and individual developers looking to get started and explore.',
      });
    case DEVANT_PAYG_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.devant.tierDescription.team',
        defaultMessage:
          'Ideal for small to mid-sized businesses with basic integration needs.',
      });
    case DEVANT_ENTERPRISE_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.devant.tierDescription.enterprise',
        defaultMessage:
          'Ideal for large enterprises with complex integration scenarios with large volumes.',
      });
    default:
      return '';
  }
}

export function getBijiraTierLabel(tierName?: string): string | undefined {
  if (tierName === BIJIRA_FREE_TRIAL_TIER) {
    return BIJIRA_FREE_TRIAL_TIER_LABEL;
  }
  return tierName;
}

export function getBijiraTierDescription(tierName: string): string {
  switch (tierName) {
    case BIJIRA_STARTER_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.bijira.tierDescription.starter',
        defaultMessage:
          'Exclusively for selected set of users who are actively using initially introduced starter tier. ',
      });
    case BIJIRA_FREE_TRIAL_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.bijira.tierDescription.freeTrial',
        defaultMessage:
          'Ideal for students and individual developers looking to get started and explore. ',
      });
    case BIJIRA_PAYG_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.bijira.tierDescription.payg',
        defaultMessage:
          'Ideal for small to mid-sized businesses with basic APIM needs. ',
      });
    case ENTERPRISE_TIER:
      return intl.formatMessage({
        id: 'utils.pageUtils.bijira.tierDescription.enterprise',
        defaultMessage:
          'Ideal for large enterprises with complex APIM scenarios with large volumes. ',
      });
    default:
      return '';
  }
}

export function getBijiraPricingDetails(tierName: string): {
  price: string;
  description: string;
} {
  switch (tierName) {
    case BIJIRA_STARTER_TIER:
      return {
        price: '$0/mo',
        description: intl.formatMessage({
          id: 'utils.pageUtils.bijira.tierPricing.starter',
          defaultMessage: '(No credit card required)',
        }),
      };
    case BIJIRA_FREE_TRIAL_TIER:
      return {
        price: '$0/mo',
        description: intl.formatMessage({
          id: 'utils.pageUtils.bijira.tierPricing.freeTrial',
          defaultMessage: '(No credit card required)',
        }),
      };
    case BIJIRA_PAYG_TIER:
      return {
        price: 'From $119/mo',
        description: intl.formatMessage({
          id: 'utils.pageUtils.bijira.tierPricing.payg',
          defaultMessage: '(Pay monthly via credit card)',
        }),
      };
    case BIJIRA_ENTERPRISE_TIER:
      return {
        price: 'Custom',
        description: intl.formatMessage({
          id: 'utils.pageUtils.bijira.tierPricing.enterprise',
          defaultMessage: '(Volume discounts with annual commitments)',
        }),
      };
    case BIJIRA_SUPPORT_TIER:
      return {
        price: '$200/month',
        description: intl.formatMessage({
          id: 'utils.pageUtils.bijira.tierPricing.support',
          defaultMessage: '+ 2% of monthly API Platform charges',
        }),
      };
    default:
      return { price: 'N/A', description: '' };
  }
}

export function getDevantPricingDetails(tierName: string): {
  price: string;
  description: string;
} {
  switch (tierName) {
    case DEVANT_STARTER_TIER:
      return {
        price: '$0',
        description: intl.formatMessage({
          id: 'utils.pageUtils.devant.tierPricing.starter',
          defaultMessage: '(Valid for 3 months)',
        }),
      };
    case DEVANT_PAYG_TIER:
      return {
        price: '$100',
        description: intl.formatMessage({
          id: 'utils.pageUtils.devant.tierPricing.payg',
          defaultMessage: 'per integration per month.',
        }),
      };
    case DEVANT_ENTERPRISE_TIER:
      return {
        price: 'Custom',
        description: intl.formatMessage({
          id: 'utils.pageUtils.devant.tierPricing.enterprise',
          defaultMessage: 'Discounts offered based on annual commitments.',
        }),
      };
    case DEVANT_SUPPORT_TIER:
      return {
        price: '$200/month',
        description: intl.formatMessage({
          id: 'utils.pageUtils.devant.tierPricing.support',
          defaultMessage: '+ 2% of monthly Devant charges',
        }),
      };
    default:
      return { price: 'N/A', description: '' };
  }
}

export function getChoreoSupportPlanAttributes(isStepBased?: boolean) {
  const supportWindow = {
    value: intl.formatMessage({
      id: 'utils.pageUtils.supportWindow',
      defaultMessage: '12x5 support window',
    }),
  };
  const communitySupport = {
    value: intl.formatMessage({
      id: 'utils.pageUtils.slackCommunity',
      defaultMessage: 'Discord Community',
    }),
  };
  const supportPortal = {
    value: intl.formatMessage({
      id: 'utils.pageUtils.supportPortal',
      defaultMessage: 'Support Portal',
    }),
  };
  const baseCharge = {
    value: intl.formatMessage(
      {
        id: 'utils.pageUtils.baseCharge',
        // eslint-disable-next-line no-template-curly-in-string
        defaultMessage: '${price} per month',
      },
      { price: choreoTierConfig.choreoSupport.basePrice }
    ),
  };
  if (isStepBased) {
    return [supportWindow, communitySupport, supportPortal];
  }
  return [baseCharge, supportPortal];
}

export function getBijiraSupportPlanAttributes() {
  const baseCharge = {
    value: intl.formatMessage(
      {
        id: 'utils.pageUtils.bijira.baseCharge',
        // eslint-disable-next-line no-template-curly-in-string
        defaultMessage: '${price} per month',
      },
      { price: 200 }
    ),
  };
  const supportPortal = {
    value: intl.formatMessage({
      id: 'utils.pageUtils.bijira.supportPortal',
      defaultMessage: 'Support Portal',
    }),
  };
  const coverage = {
    value: intl.formatMessage({
      id: 'utils.pageUtils.bijira.coverage',
      defaultMessage: '12 x 5 Coverage',
    }),
  };
  return [baseCharge, supportPortal, coverage];
}

export function getDevantSupportPlanAttributes() {
  const baseCharge = {
    value: intl.formatMessage(
      {
        id: 'utils.pageUtils.devant.baseCharge',
        // eslint-disable-next-line no-template-curly-in-string
        defaultMessage: '${price} per month',
      },
      { price: 200 }
    ),
  };
  const supportPortal = {
    value: intl.formatMessage({
      id: 'utils.pageUtils.devant.supportPortal',
      defaultMessage: 'Support Portal',
    }),
  };
  const coverage = {
    value: intl.formatMessage({
      id: 'utils.pageUtils.devant.coverage',
      defaultMessage: '12 x 5 Coverage',
    }),
  };
  return [baseCharge, supportPortal, coverage];
}

export function getProcessedChoreoStepBasedPaygAttributes(): IComplexTierAttribute[] {
  const attributes = [
    {
      value: intl.formatMessage(
        {
          id: 'utils.pageUtils.tierCard.attribute.choreo.payAsYouGo.freeSteps',
          defaultMessage: '{count} Free steps',
        },
        { count: choreoTierConfig.stepBased.payAsyouGo.freeSteps }
      ),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tierCard.attribute.choreo.usage',
        defaultMessage: 'Pay only for usage',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tierCard.attribute.choreo.team.renewal',
        defaultMessage: 'Monthly renewal',
      }),
    },
  ];
  return attributes;
}

export function getProcessedChoreoTierAttributes(
  tierName?: string
): IComplexTierAttribute[] {
  const enterpriseInfraInfo = () => (
    <div>
      <Typography>
        <FormattedMessage
          id="utils.pageUtils.tier.attribute.choreo.enterprise.infraInfo"
          defaultMessage={
            'We pass through actual costs or directly bill your enterprise, ' +
            'with no free {productName} Data Plane credits. ' +
            'These include vCPU, RAM, storage, Ingress, and Egress network charges.'
          }
          values={{ productName: PRODUCT_NAME }}
        />
      </Typography>
    </div>
  );

  const developerTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.developerTier.attribute1',
        defaultMessage: 'First 5 components FREE',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.developerTier.attribute2',
        defaultMessage: '$5/additional component monthly',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.developerTier.attribute3',
        defaultMessage: '$100 infrastructure credits per month free',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.developerTier.attribute4',
        defaultMessage: 'Unlimited projects',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.developerTier.attribute5',
        defaultMessage: '2 developers',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.developerTier.attribute7',
        defaultMessage:
          'Scales down to 0 when not in use and up to 1 pod when needed',
      }),
    },
  ];

  const paygTierAttributes = [
    {
      value: intl.formatMessage(
        {
          id: 'utils.pageUtils.tier.attributes.payg.attribute1',
          // eslint-disable-next-line no-template-curly-in-string
          defaultMessage: '${price}/component monthly',
        },
        { price: choreoTierConfig.payAsyouGo.pricePerComponent }
      ),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.payg.attribute2',
        defaultMessage:
          '5 components and $1000 infrastructure credits FREE for 6 months if you sign up with a work email',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.payg.attribute3',
        defaultMessage: 'Unlimited projects',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.payg.attribute4',
        defaultMessage: 'Unlimited developers',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.payg.attribute6',
        defaultMessage: 'Scale up without restrictions',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.payg.attribute7',
        defaultMessage: 'You decide how to scale down',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.payg.attribute8',
        defaultMessage: 'Supports private data planes',
      }),
    },
  ];

  const enterpriseTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.enterprise.attribute1',
        defaultMessage: 'Discounts offered based on annual commitments ',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.enterprise.attribute2',
        defaultMessage: 'Pass-through infrastructure costs',
      }),
      info: enterpriseInfraInfo(),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.enterprise.attribute3',
        defaultMessage: 'Unlimited projects',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.enterprise.attribute4',
        defaultMessage: 'Unlimited developers',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.enterprise.attribute6',
        defaultMessage: 'Scale up without restrictions',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.enterprise.attribute7',
        defaultMessage: 'You decide how to scale down',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.enterprise.attribute8',
        defaultMessage: 'Supports private data planes',
      }),
    },
  ];

  const paygWithSupportTierAttributes = [...paygTierAttributes];
  getChoreoSupportPlanAttributes(true).forEach((attribute) => {
    paygWithSupportTierAttributes.push(attribute);
  });

  const freeTrialTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.freeTrial.attribute1',
        defaultMessage: '5 components',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.freeTrial.attribute2',
        defaultMessage: '14-day free trial',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.tier.attributes.freeTrial.attribute3',
        defaultMessage: 'Credit card required',
      }),
    },
  ];

  switch (tierName) {
    case TEAM_TIER:
      return paygTierAttributes;
    case DEVELOPER_TIER:
      return developerTierAttributes;
    case FREE_TRIAL_TIER:
      return freeTrialTierAttributes;
    case ENTERPRISE_TIER:
      return enterpriseTierAttributes;
    case TEAM_WITH_SUPPORT_TIER:
      return paygWithSupportTierAttributes;
    default:
      return [];
  }
}

export function getProcessedBijiraTierAttributes(
  tierName?: string
): IComplexTierAttribute[] {
  const starterTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.starter.attribute1',
        defaultMessage: 'Starter Tier',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.starter.attribute2',
        defaultMessage: 'SaaS-hosted API Control Plane',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.starter.attribute3',
        defaultMessage: 'Limited product access',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.starter.attribute4',
        defaultMessage: 'Limited usage',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.starter.attribute5',
        defaultMessage: 'Community support',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.starter.attribute6',
        defaultMessage: 'Self service sign-up and get started',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.starter.attribute7',
        defaultMessage: 'No contracts required',
      }),
    },
  ];
  const freeTrialTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.30dayTrial.attribute1',
        defaultMessage: '30-Day Free Trial',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.30dayTrial.attribute2',
        defaultMessage: 'SaaS-hosted API Control Plane',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.30dayTrial.attribute3',
        defaultMessage: 'Limited product access',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.30dayTrial.attribute4',
        defaultMessage: 'Limited usage',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.30dayTrial.attribute5',
        defaultMessage: 'Community support',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.30dayTrial.attribute6',
        defaultMessage: 'Self service sign-up and get started',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.30dayTrial.attribute7',
        defaultMessage: 'No contracts required',
      }),
    },
  ];

  const paygTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.payg.attribute1',
        defaultMessage: 'Pay-As-You-Go',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.payg.attribute2',
        defaultMessage: 'SaaS-hosted API Control Plane',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.payg.attribute3',
        defaultMessage: 'Usage-based access',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.payg.attribute4',
        defaultMessage: 'Volume based pricing',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.payg.attribute5',
        defaultMessage: 'Basic support (charges may apply)',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.payg.attribute6',
        defaultMessage: 'Self service sign-up and get started',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.payg.attribute7',
        defaultMessage: 'No contracts required',
      }),
    },
  ];

  const enterpriseTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.enterprise.attribute1',
        defaultMessage: 'Enterprise',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.enterprise.attribute2',
        defaultMessage: 'SaaS or Self-Hosted API Control Plane',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.enterprise.attribute3',
        defaultMessage: 'Full product access',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.enterprise.attribute4',
        defaultMessage: 'Unlimited usage',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.enterprise.attribute5',
        defaultMessage: 'Enterprise support',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.enterprise.attribute6',
        defaultMessage: 'Annual contract commitments',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.enterprise.attribute7',
        defaultMessage: 'Pay annually via invoice',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.bijira.tier.attributes.enterprise.attribute8',
        defaultMessage: 'Annual contract with dedicated account management',
      }),
    },
  ];

  const paygWithSupportTierAttributes = [...paygTierAttributes];
  getBijiraSupportPlanAttributes().forEach((attribute) => {
    paygWithSupportTierAttributes.push(attribute);
  });

  switch (tierName) {
    case BIJIRA_PAYG_TIER:
      return paygTierAttributes;
    case BIJIRA_STARTER_TIER:
      return starterTierAttributes;
    case BIJIRA_FREE_TRIAL_TIER:
      return freeTrialTierAttributes;
    case BIJIRA_ENTERPRISE_TIER:
      return enterpriseTierAttributes;
    default:
      return [];
  }
}

export function getProcessedDevantTierAttributes(
  tierName?: string
): IComplexTierAttribute[] {
  const starterTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.starter.attribute1',
        defaultMessage: '5 Free Integrations for 3 months',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.starter.attribute2',
        defaultMessage: 'Full product access',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.starter.attribute3',
        defaultMessage: 'Limited usage',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.starter.attribute4',
        defaultMessage: '1M transactions per month',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.starter.attribute5',
        defaultMessage: '100 Automation Core minutes per month',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.starter.attribute6',
        defaultMessage: 'No contracts required',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.starter.attribute7',
        defaultMessage: 'Self service—sign up and get started',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.starter.attribute8',
        defaultMessage: 'No credit card required',
      }),
    },
  ];

  const paygTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.payg.attribute1',
        defaultMessage: '$100 per integration per month',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.payg.attribute2',
        defaultMessage: 'Full product access',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.payg.attribute3',
        defaultMessage: 'No contracts required',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.payg.attribute4',
        defaultMessage: 'Self service—sign up and get started',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.payg.attribute5',
        defaultMessage: 'Pay monthly via credit card',
      }),
    },
  ];

  const enterpriseTierAttributes = [
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.enterprise.attribute1',
        defaultMessage: 'Discounts offered based on annual commitments',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.enterprise.attribute2',
        defaultMessage: 'Full product access',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.enterprise.attribute3',
        defaultMessage: 'Unlimited usage',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.enterprise.attribute4',
        defaultMessage: 'Annual contract commitments',
      }),
    },
    {
      value: intl.formatMessage({
        id: 'utils.pageUtils.devant.tier.attributes.enterprise.attribute5',
        defaultMessage: 'Pay annually via invoice',
      }),
    },
  ];

  const paygWithSupportTierAttributes = [...paygTierAttributes];
  getBijiraSupportPlanAttributes().forEach((attribute) => {
    paygWithSupportTierAttributes.push(attribute);
  });

  switch (tierName) {
    case DEVANT_PAYG_TIER:
      return paygTierAttributes;
    case DEVANT_STARTER_TIER:
      return starterTierAttributes;
    case DEVANT_ENTERPRISE_TIER:
      return enterpriseTierAttributes;
    default:
      return [];
  }
}

export const getRestructuredValues = (value: string | number | undefined) => {
  if (typeof value === 'number') {
    const valueInMillions = value / 1000000;
    const valueInThousands = value / 1000;
    if (valueInMillions >= 1) {
      return `${valueInMillions}M`;
    }
    if (valueInThousands >= 1) {
      return `${valueInThousands}K`;
    }
  }
  return value;
};

/**
 * Get the relevant card logo for card brand.
 * @param cardBrand - card brandname.
 * According to Stripe; card brand can be `American Express`, `Diners Club`, `Discover`, `JCB`,
 * `MasterCard`, `UnionPay`, `Visa`, or `Unknown`.
 * @returns the relevant logo for the card brand.
 */
export function getCardBrandImage(cardBrand: string) {
  if (!cardBrand) return GenericCardLogo;

  switch (cardBrand.toLowerCase()) {
    case 'visa':
      return VisaCardLogo;
    case 'mastercard':
      return MasterCardLogo;
    case 'amex':
      return AmexCardLogo;
    case 'discover':
      return DiscoverCardLogo;
    case 'unionpay':
      return UnionPayCardLogo;
    default:
      return GenericCardLogo;
  }
}

export function getCardBrandDisplayName(cardBrand: string) {
  if (!cardBrand) return undefined;

  switch (cardBrand.toLowerCase()) {
    case 'visa':
      return 'Visa';
    case 'mastercard':
      return 'MasterCard';
    case 'amex':
      return 'American Express';
    case 'discover':
      return 'Discover';
    case 'unionpay':
      return 'UnionPay';
    default:
      return undefined;
  }
}

export const isInEnterpriseTier = (
  tier: SubscriptionTier | undefined,
  cloudType: string
): boolean => {
  // Enterprise Tier id is constant for Choreo.
  if (cloudType === CloudType.Choreo && tier?.tierId === enterpriseTierUuid) {
    return true;
  }

  // Enterprise Tier name starts with "custom" for Asgardeo.
  if (cloudType === CloudType.Asgardeo && tier?.name?.startsWith('custom')) {
    return true;
  }

  return false;
};

export const getAzureBasedSubCancelContent = () =>
  intl.formatMessage({
    id: 'utils.pageUtils.currentPlan.cancel.dialog.content.azureBased',
    defaultMessage:
      'Your subscription will be downgraded to Developer plan immediately and ' +
      'the respective Azure subscription will be cancelled in a few minutes. ' +
      'You may check the Azure subscription status in the Azure portal. ' +
      'Do you want to cancel the subscription?',
  });

export const getCancelDialogContent = (
  subscriptionType: ChoreoSubscriptionType,
  tierName?: string
) => {
  if (subscriptionType === ChoreoSubscriptionType.ChoreoSubscription) {
    if (tierName === TEAM_TIER) {
      return intl.formatMessage({
        id: 'utils.pageUtils.currentPlan.cancel.dialog.content.paygTier',
        defaultMessage:
          'Your subscription will be downgraded to Developer plan immediately and ' +
          'if there are no other active paid subscriptions, any associated support plans will also be ' +
          'cancelled at the end of the current month. ' +
          'Do you want to cancel the subscription?',
      });
    }
    if (tierName === DEVELOPER_TIER) {
      return intl.formatMessage({
        id: 'utils.pageUtils.currentPlan.cancel.dialog.content.developerTier',
        defaultMessage:
          'Canceling your subscription may result in reverting to free tier restrictions, limiting features and ' +
          'access. Do you want to cancel the subscription?',
      });
    }
  }
  if (subscriptionType === ChoreoSubscriptionType.ChoreoSupport) {
    return intl.formatMessage({
      id: 'utils.pageUtils.currentPlan.cancel.dialog.content.choreoSupport',
      defaultMessage:
        'Please note that the support subscription will be cancelled at the end of ' +
        'the current month. Do you want to cancel the support subscription?',
    });
  }
  return '';
};

export const getBijiraCancelDialogContent = (
  subscriptionType: BijiraSubscriptionType,
  tierName?: string
) => {
  if (subscriptionType === BijiraSubscriptionType.BijiraSubscription) {
    if (tierName === BIJIRA_PAYG_TIER) {
      return intl.formatMessage({
        id: 'utils.pageUtils.currentPlan.cancel.dialog.content.bijira.paygTier',
        defaultMessage:
          'Your subscription will be downgraded to Starter plan immediately and ' +
          'if there are no other active paid subscriptions, any associated support plans will also be ' +
          'cancelled at the end of the current month. ' +
          'Do you want to cancel the subscription?',
      });
    }
  }
  if (subscriptionType === BijiraSubscriptionType.BijiraSupport) {
    return intl.formatMessage({
      id: 'utils.pageUtils.currentPlan.cancel.dialog.content.bijira.support',
      defaultMessage:
        'Please note that the support subscription will be cancelled at the end of ' +
        'the current month. Do you want to cancel the support subscription?',
    });
  }
  return '';
};

export const getDevantCancelDialogContent = (
  subscriptionType: DevantSubscriptionType,
  tierName?: string
) => {
  if (subscriptionType === DevantSubscriptionType.DevantSubscription) {
    if (tierName === DEVANT_PAYG_TIER) {
      return intl.formatMessage({
        id: 'utils.pageUtils.currentPlan.cancel.dialog.content.devant.paygTier',
        defaultMessage:
          'Your subscription will be downgraded to Starter plan immediately and ' +
          'if there are no other active paid subscriptions, any associated support plans will also be ' +
          'cancelled at the end of the current month. ' +
          'Do you want to cancel the subscription?',
      });
    }
  }
  if (subscriptionType === DevantSubscriptionType.DevantSupport) {
    return intl.formatMessage({
      id: 'utils.pageUtils.currentPlan.cancel.dialog.content.devant.support',
      defaultMessage:
        'Please note that the support subscription will be cancelled at the end of ' +
        'the current month. Do you want to cancel the support subscription?',
    });
  }
  return '';
};

export const getAsgardeoTermsContent = () => [
  {
    id: 'PYG',
    text: intl.formatMessage({
      id: 'utils.pageUtils.terms.pyg',
      defaultMessage:
        'Your Subscription includes monthly commitments for the resources You select under the ' +
        '"Upfront Commitments" section of the Usage page. These resources are billed in advance. ' +
        'Any usage above the committed amounts will be charged as overage at the applicable rates.\n\n' +
        'Resources listed under the "Pay-per-Use" section are charged based on actual usage beyond ' +
        'any included limits.\n\n' +
        'The applicable fees and rates are those shown in the Usage page.\n\n' +
        'By subscribing, You authorize WSO2 to automatically charge Your payment method each month ' +
        'for both the committed fees and any usage-based charges. Usage-based charges may vary from ' +
        'month to month depending on Your actual usage.',
    }),
  },
  {
    id: 'auto-renewal',
    text: intl.formatMessage({
      id: 'utils.pageUtils.terms.autoRenewal',
      defaultMessage:
        'The subscription will automatically renew monthly until cancelled.',
    }),
  },
  {
    id: 'price-changes',
    text: intl.formatMessage({
      id: 'asgardeo.terms.priceChanges',
      defaultMessage:
        'You will be notified at least 30 days before any change to your committed fees or overage rates takes effect.',
    }),
  },
  {
    id: 'cancellation',
    text: intl.formatMessage({
      id: 'asgardeo.terms.cancellation',
      defaultMessage:
        'You may cancel your monthly subscription at any time by clicking the "Cancel" button in this console. ' +
        'Cancellation takes effect immediately. ' +
        'Your committed fees for the current billing period are non-refundable, ' +
        'and you will be charged for any usage and overage incurred up to the cancellation date.',
    }),
  },
];

export const getTermsContent = () => [
  {
    id: 'auto-renewal',
    text: intl.formatMessage({
      id: 'utils.pageUtils.terms.autoRenewal',
      defaultMessage:
        'The subscription will automatically renew monthly until cancelled.',
    }),
  },
  {
    id: 'price-changes',
    text: intl.formatMessage({
      id: 'utils.pageUtils.terms.priceChanges',
      defaultMessage:
        'You will be notified of any price changes at least 7 days before they take effect.',
    }),
  },
  {
    id: 'cancellation',
    text: intl.formatMessage({
      id: 'utils.pageUtils.terms.cancellation',
      defaultMessage:
        'You may cancel your monthly subscription at any time by clicking the "Cancel" button in this console. ' +
        'The service subscription will be cancelled immediately, and you will be charged only for the portion ' +
        'of the service used up to that date. In the case of a support subscription, the cancellation will ' +
        'come into effect at the end of the month.',
    }),
  },
];
